import { Skeleton } from "@mui/material";
import { useTheme } from "@mui/system";
import { Cross, MailEmailLetter } from "@pmag/icons";
import { Button, Typography } from "@pmag/ui-components";
import { useAttributeConfig } from "@src/hooks/useAttributeConfig";
import { AddToCartButton } from "@src/pages/subGroupDetail/subgroupDetailsListSection/addToCartButton/AddToCartButton";
import { AvailabilityStatus } from "@src/pages/subGroupDetail/subgroupDetailsListSection/availabilityStatus/AvailabilityStatus";
import { StyledTypography } from "@src/pages/subGroupDetail/subgroupDetailsListSection/sparePartsList/SparePartsList.styles";
import { WishlistIconButton } from "@src/pages/subGroupDetail/subgroupDetailsListSection/wishlistIconButton/WishlistIconButton";
import { dispatchSendRequestEvent, type SPFArticleWithPriceInfo, SPFAvailabilityStatus } from "@src/utils/events";
import * as React from "react";
import { type SyntheticEvent } from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";

import {
    ArticleImage,
    ArticleInfoBottomContainer,
    ArticleInfoContainer,
    BottomContainer,
    CloseButton,
    LineNumberChip,
    RightContainer,
    RootContainer,
    StyledSelect,
    TopContainer,
} from "./SmallListItem.styles";

interface SmallListItemProps {
    brand: string;
    clientWishlist: string[];
    modelId: string;
    onClose?: () => void;
    showBorderBottom: boolean;
    sparePart: SPFArticleWithPriceInfo;
}

export const SmallListItem: React.FunctionComponent<SmallListItemProps> = ({
    brand,
    modelId,
    sparePart,
    showBorderBottom,
    clientWishlist,
    onClose,
}) => {
    const theme = useTheme();
    const { hasArticleInfos, hasAddToCart, priceInfoDisclaimer, quantityInfoDisclaimer, sendRequestEnabled } = useAttributeConfig();
    const [amount, setAmount] = React.useState(sparePart.quantity > 0 ? sparePart.quantity : 1);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        setTimeout(() => {
            setLoading(false);
        }, 5000);
    }, []);

    const onCloseClick = (event: SyntheticEvent) => {
        event.stopPropagation();
        onClose?.();
    };

    const showAddToCartButton =
        hasArticleInfos &&
        hasAddToCart &&
        (sparePart.clientArticleInfo?.availability === SPFAvailabilityStatus.Available ||
            sparePart.clientArticleInfo?.availability === SPFAvailabilityStatus.ManufacturerAvailable);

    const showSendRequestButton =
        sendRequestEnabled &&
        (!hasArticleInfos ||
            sparePart.clientArticleInfo?.availability === SPFAvailabilityStatus.NotAvailable ||
            sparePart.clientArticleInfo?.availability === SPFAvailabilityStatus.Unknown);

    return (
        <RootContainer showBorderBottom={showBorderBottom}>
            <TopContainer>
                {sparePart.article?.assetPath && <ArticleImage src={sparePart.article.assetPath} />}

                <ArticleInfoContainer>
                    <Typography variant="heading-08">
                        {sparePart.article?.name}

                        {sparePart.quantity && (
                            <StyledTypography component="span" variant="body-short-01" color={theme.palette.core.grey._60}>
                                {`(${sparePart.quantity})`}

                                {quantityInfoDisclaimer && <sup>**</sup>}
                            </StyledTypography>
                        )}
                    </Typography>

                    <Typography variant="helper-text-01">{sparePart.article?.articleNumber}</Typography>

                    {loading && !sparePart.clientArticleInfo ? (
                        <ArticleInfoBottomContainer>
                            <Skeleton height={24} width={100} />

                            <Skeleton height={24} width={50} />
                        </ArticleInfoBottomContainer>
                    ) : (
                        <ArticleInfoBottomContainer>
                            {sparePart.clientArticleInfo?.availability && (
                                <AvailabilityStatus
                                    availabilityStatus={sparePart.clientArticleInfo?.availability}
                                    customText={sparePart.clientArticleInfo?.availabilityText}
                                />
                            )}

                            <Typography variant="heading-08">
                                {sparePart.clientArticleInfo && sparePart.clientArticleInfo.price ? (
                                    <>
                                        <FormattedNumber
                                            value={sparePart.clientArticleInfo.price}
                                            currency={sparePart.clientArticleInfo.currency}
                                            style="currency"
                                        />

                                        {priceInfoDisclaimer && <sup>*</sup>}
                                    </>
                                ) : (
                                    <FormattedMessage id="sparePartsFinder.subgroupPage.sparePartsList.placeholder" defaultMessage="-" />
                                )}
                            </Typography>
                        </ArticleInfoBottomContainer>
                    )}
                </ArticleInfoContainer>
            </TopContainer>

            <BottomContainer>
                <LineNumberChip>
                    <Typography variant="heading-09" color={theme.palette.core.white}>
                        {sparePart.lineNumber}
                    </Typography>
                </LineNumberChip>

                <RightContainer>
                    {showAddToCartButton && sparePart.article && (
                        <>
                            <StyledSelect
                                select
                                value={amount}
                                onChange={(value) => setAmount(parseInt(value.target.value))}
                                variant="outlined"
                                SelectProps={{ native: true }}
                            >
                                {Array(20)
                                    .fill(null)
                                    .map((_, index) => (
                                        <option key={`amount-select-${index}`} value={index + 1}>{`${index + 1}`}</option>
                                    ))}
                            </StyledSelect>

                            <AddToCartButton
                                articleNumber={sparePart.article.articleNumber}
                                brand={brand}
                                amount={amount}
                                clientArticleNumber={sparePart.clientArticleInfo?.clientArticleNumber}
                            />
                        </>
                    )}

                    {showSendRequestButton && sparePart.article && (
                        <Button
                            variant="secondary"
                            size="small"
                            icon={<MailEmailLetter size="small" />}
                            onClick={() => sparePart.article && dispatchSendRequestEvent(sparePart.article.articleNumber, brand)}
                        >
                            <FormattedMessage id="sparePartsFinder.subgroupPage.sparePartsList.sendRequest" defaultMessage="Send request" />
                        </Button>
                    )}

                    <WishlistIconButton brand={brand} modelId={modelId} item={sparePart} clientWishlist={clientWishlist} />
                </RightContainer>
            </BottomContainer>

            {onClose && (
                <CloseButton onClick={onCloseClick} variant="transparent">
                    <Cross size="small" />
                </CloseButton>
            )}
        </RootContainer>
    );
};
